'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import getListingPageListName from '@helpers/analyticsHelpers/getListingPageListName';
import Text from '@atoms/Text/Text';
import ProductContainer from '@molecules/ProductContainer/ProductContainer';
import Breadcrumbs from '@molecules/Breadcrumbs/Breadcrumbs';
import FilterAndSort from '@organisms/FilterAndSort/FilterAndSort';
import useResponsiveProductLimit from '@hooks/useResponsiveProductLimit';
import useStockChange from '@hooks/useStockChange';
import useGridTemplateColumns from '@hooks/useGridTemplateColumns';
import Spinner from '@molecules/Spinner/Spinner';
import { trackShownMoreProducts, trackSortedProducts } from '@helpers/analyticsHelpers/trackCategoryProductBanner';
import packageComponentHelper from '@helpers/packageComponentHelper';
import useCategoryMetadata from '@hooks/useCategoryMetadata';
import SubCategoryLinks from '@molecules/SubCategoryLinks/SubCategoryLinks';
import useResponsive from '@hooks/useResponsive';
import useCmsComponents from '@hooks/useCmsComponents';
import ProductBeamSkeleton from '@molecules/ProductBeam/ProductBeam.skeleton';
import type { AxfoodBasicProductViewModel } from '@occ/api-client';
import useCategory from '@hooks/useCategory';
import useMediaQueryKey from '@hooks/useMediaQueryKey';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useCategoryCode } from '@root/contexts/CategoryContext';
import {
  BreadcrumbsWrapper,
  HeadingTitle,
  HeadingWrapper,
  StyledAmountText,
  StyledAlert,
} from './CategoryProductBannerComponent.styles';

interface Props {
  data: ProductBannerComponentType;
}

const CategoryProductBannerComponent = ({ data }: Props) => {
  const packageHelper = packageComponentHelper();
  const categoryCode = useCategoryCode();
  const { fromDesktop, isMobile } = useResponsive();
  const t = useTranslations('categoryPage');
  const ref = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<string>('');
  const { products, refreshCategories, isLoading, breadcrumbs } = useCategory({ sort, categoryPath: categoryCode });
  const [itemsPerRow, setItemsPerRow] = useState(0);
  const [totalVisibleItems, setTotalVisibleItems, getPageLimit] = useResponsiveProductLimit('CATEGORY') as any;
  const { data: gridPackages } = useCmsComponents(JSON.stringify(data?.gridPackages?.replace(/[[\]"]+/g, '')));
  const getCurrentMediaQueryKey = useMediaQueryKey();

  useCategoryMetadata(products.categoryInfo);
  useGridTemplateColumns(setItemsPerRow, ref, isLoading);
  useStockChange(() => {
    refreshCategories();
  });

  const getSortOptions = () => {
    return products?.sorts?.map((sort) => {
      const { code: value, name: label, selected } = sort;
      return { value: value as string, label: label as string, selected };
    });
  };
  const sortOptions = getSortOptions();
  const getSelectedSort = () => sortOptions?.find((s) => s.selected);

  const onSort = (option: OptionType) => {
    const selectedSort = getSelectedSort();
    if (selectedSort && option.value !== selectedSort.value) {
      setSort(option.value);
      trackSortedProducts(option.label);
    }
  };

  const loadMore = async () => {
    await products.loadMore();
    trackShownMoreProducts({ categories: categoryCode });
    setTotalVisibleItems(totalVisibleItems + getPageLimit());
  };

  const combinedProductsAndGridPackages = () => {
    if (gridPackages?.component && products.results) {
      return packageHelper.positionPackagesInList(
        products.results as AxfoodBasicProductViewModel[],
        gridPackages?.component as ImageGridComponentType[],
        getCurrentMediaQueryKey(),
        itemsPerRow
      );
    }
    return products.results as AxfoodBasicProductViewModel[];
  };

  // Infinite scroll hook
  const [infiniteRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: products.hasMorePages,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  return (
    <>
      <div ref={ref}>
        {products.superCategories && products.superCategories.length > 0 && (
          <BreadcrumbsWrapper data-testid="category-breadcrumbs" role="navigation" aria-label={t('breadcrumbLabel')}>
            <Breadcrumbs breadcrumbs={[...products.superCategories.slice(1), { name: products?.categoryInfo?.name }]} />
          </BreadcrumbsWrapper>
        )}
        {!isMobile && products.categoryInfo && (
          <HeadingWrapper>
            <HeadingTitle data-testid="category-heading" variant="h1">
              {products?.categoryInfo?.name}
            </HeadingTitle>
            <Text type="body" size="small" data-testid="category-total-amount" role="status">
              {t('totalResults', { totalResult: products?.pagination?.totalNumberOfResults || 0 })}
            </Text>
          </HeadingWrapper>
        )}

        {products.subCategories && !fromDesktop && <SubCategoryLinks categories={products?.subCategories} />}

        <FilterAndSort
          filterOptions={products.facets}
          sortOptions={sortOptions}
          selectedFilters={breadcrumbs}
          onSort={onSort}
          totalResults={products?.pagination?.totalNumberOfResults}
        />

        {isMobile && products?.categoryInfo && (
          <StyledAmountText type="body" size="small" data-testid="category-total-amount" role="status">
            {t('totalResults', { totalResult: products?.pagination?.totalNumberOfResults || 0 })}
          </StyledAmountText>
        )}

        {products.results && products.results.length > 0 && (
          <>
            <ProductContainer
              title={getListingPageListName('category_page', categoryCode, breadcrumbs)}
              products={combinedProductsAndGridPackages()}
              productType="online"
              offline={false}
              numberOfGridCols={itemsPerRow}
            />
            {products.hasMorePages && <Spinner size={32} ref={infiniteRef} />}
          </>
        )}

        {products?.pagination?.totalNumberOfResults === 0 && (
          <StyledAlert hasCloseButton={false} theme="info" id="noProductsInCategory">
            <Text type="body" size="small" color="black">
              {t('noProductsToShow')}
            </Text>
          </StyledAlert>
        )}
      </div>
      {!(products.results && products?.results?.length > 0) && products?.pagination?.totalNumberOfResults !== 0 && (
        <ProductBeamSkeleton type="categoryBeam" rows={2} />
      )}
    </>
  );
};

export default CategoryProductBannerComponent;
